/**
 * QP-172 — Flow navigation context.
 *
 * Allows child components (e.g. PortfolioList) to trigger programmatic
 * navigation to a specific goal + portfolio tab from anywhere in the
 * flow tree. The wizard-level provider stores the pending target; the
 * goal-context layer consumes it and auto-selects the portfolio tab.
 */

import { createContext, useCallback, useContext, useRef, useState } from "react";

type PendingNavigation = { goalId: string; portfolioId: string } | null;

type FlowNavigationContextValue = {
  /** Navigate to a specific portfolio under a specific goal. */
  navigateToPortfolio: (goalId: string, portfolioId: string) => void;
  /** Read the pending portfolio navigation for a given goal without clearing it. */
  peekPendingPortfolio: (goalId: string) => string | null;
  /** Clear the pending navigation (call after successful navigation). */
  clearPendingNavigation: () => void;
};

const FlowNavigationContext = createContext<FlowNavigationContextValue | null>(null);

export function FlowNavigationProvider({
  onGoalChange,
  children,
}: {
  /** Callback to switch the L1 goal tab (owned by GoalListWidget / AdvisorGoalNav). */
  onGoalChange: (goalId: string) => void;
  children: React.ReactNode;
}) {
  const pendingRef = useRef<PendingNavigation>(null);
  // Trigger re-render in consumers that need to pick up the pending nav.
  const [, setTick] = useState(0);

  const navigateToPortfolio = useCallback(
    (goalId: string, portfolioId: string) => {
      pendingRef.current = { goalId, portfolioId };
      onGoalChange(goalId);
      setTick((t) => t + 1);
    },
    [onGoalChange],
  );

  const peekPendingPortfolio = useCallback((goalId: string): string | null => {
    const pending = pendingRef.current;
    if (pending && pending.goalId === goalId) return pending.portfolioId;
    return null;
  }, []);

  const clearPendingNavigation = useCallback(() => {
    pendingRef.current = null;
  }, []);

  return (
    <FlowNavigationContext.Provider
      value={{ navigateToPortfolio, peekPendingPortfolio, clearPendingNavigation }}
    >
      {children}
    </FlowNavigationContext.Provider>
  );
}

/** Use the flow navigation context (returns null when outside a provider). */
export function useFlowNavigation(): FlowNavigationContextValue | null {
  return useContext(FlowNavigationContext);
}
